import * as React from 'react'
import { createPortal } from 'react-dom'

const Toast: React.FC<{
  children?: JSX.Element | string
  isVisible: boolean
  onClose: () => void
  type: 'success'
  closeButtonAriaLabel?: string
}> = ({ children, isVisible, onClose, type, closeButtonAriaLabel = 'Close' }) => {
  const toastContainerReference = React.useRef<HTMLDivElement>()
  const lastVisibleValue = React.useRef<boolean>(false)
  const timeout = React.useRef<number>()

  /**
   * adds the toast container to the page
   */
  React.useLayoutEffect(() => {
    const container = document.createElement('div')
    container.classList.add('cmp-toast', `cmp-toast--${type}`)

    document.body.append(container)

    toastContainerReference.current = container

    return () => {
      container.remove()
    }
  }, [])

  /**
   * listens to change in isVisible to refresh or set the timeout
   * also takes care of clearing the timeout if the component gets destroyed
   * NOTE: Auto-dismiss is disabled for accessibility compliance (WCAG - Timing Adjustable)
   */
  React.useEffect(() => {
    if (!lastVisibleValue.current && isVisible && timeout.current) {
      // visibility just turned on
      window.clearTimeout(timeout.current)

      // DO NOT set timeout - notification stays visible until user closes it
      // This is required for WCAG compliance
    }

    lastVisibleValue.current = isVisible

    return () => {
      if (timeout.current) {
        window.clearTimeout(timeout.current)
      }
    }
  }, [isVisible])

  /**
   * syncs visibility with isVisible prop
   */
  if (toastContainerReference.current) {
    toastContainerReference.current.classList.remove('cmp-toast--is-visible')
    if (isVisible) {
      toastContainerReference.current.classList.add('cmp-toast--is-visible')
    }
  }

  if (!isVisible || !toastContainerReference.current) {
    return null
  }

  function handleCloseClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation()
    event.preventDefault()
    onClose()
  }

  return createPortal(
    <div className="cmp-toast__content">
      <i className="ri-checkbox-circle-line cmp-toast__icon" />
      <span className="cmp-toast__text">{children}</span>
      <button
        className="cmp-toast__close-button"
        onClick={handleCloseClick}
        aria-label={closeButtonAriaLabel}
        type="button"
      >
        <i className="ri-close-line" />
      </button>
    </div>,
    toastContainerReference.current,
  )
}

export default Toast
